/* eslint-disable react/jsx-no-undef */
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// @mui
import { useTheme } from '@mui/material/styles';
import { Stack, Card, Typography, Box, useMediaQuery, IconButton } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import KeyIcon from '@mui/icons-material/Key';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// components
import { useEffect } from 'react';
import { FormProvider, RHFTextField } from '../../../../components/hook-form';
import axios from '../../../../utils/axios';
import useAuth from '../../../../hooks/useAuth';

// ----------------------------------------------------------------------

export default function AccountChangePassword() {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isLaptop = useMediaQuery(theme.breakpoints.up('md')); // Check if screen width is >= 900px

  const ChangePassWordSchema = Yup.object().shape({
    oldPassword: Yup.string().required('OLD PASSWORD IS REQUIRED'),
    newPassword: Yup.string().min(6, 'PASSWORD MUST BE AT LEAST 6 CHARACTERS').required('NEW PASSWORD IS REQUIRED'),
    confirmNewPassword: Yup.string()
      .oneOf([Yup.ref('newPassword'), null], 'CONFIRM PASSWORD AND NEW PASSWORD MUST MATCH')
      .required('CONFIRM PASSWORD IS REQUIRED'),
  });

  const defaultValues = {
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  };

  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(ChangePassWordSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (payload) => {
    try {
      console.log('PAYLOAD', payload);
      const response = await axios.put('/user/change-password', payload);
      reset();
      enqueueSnackbar(response?.data?.message.toUpperCase());
    } catch (error) {
      enqueueSnackbar(error.message.toUpperCase(), { variant: 'error' }); // Displaying error message
    }
  };

  // Prevent scrolling on mount
  useEffect(() => {
    document.body.style.overflow = 'hidden'; // Disable scroll
    return () => {
      document.body.style.overflow = 'auto'; // Re-enable scroll when component unmounts
    };
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '90vh',
        p: 2,
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(to bottom, rgba(22,28,36,0.8), rgba(22,28,36,0.6))'
          : 'linear-gradient(to bottom, rgba(245,247,250,0.8), rgba(245,247,250,0.6))',
      }}
    >
      <Card
        sx={{
          p: { xs: 2, md: 3 },
          maxWidth: 420,
          width: '100%',
          position: 'relative',
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          borderRadius: 2,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(45,45,60,0.95), rgba(45,45,60,0.9))'
            : 'white',
        }}
      >

        {/* Back Button - Only shown for client role */}
        {user.role.rolename === 'client' && (
          <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 1 }}>
            <IconButton
              onClick={() => navigate('/client/profile')}
              sx={{
                width: 35,
                height: 35,
                bgcolor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.1)'
                  : 'rgba(0,0,0,0.05)',
                backdropFilter: 'blur(6px)',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.2)'
                    : 'rgba(0,0,0,0.1)',
                  transform: 'translateX(-3px)',
                },
                transition: 'all 0.2s',
                border: `1px solid ${theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.12)'
                  : 'rgba(0,0,0,0.08)'
                  }`,
              }}
            >
              <ArrowBackIcon
                sx={{
                  fontSize: 20,
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.8)'
                    : 'rgba(0,0,0,0.7)'
                }}
              />
            </IconButton>
          </Box>
        )}

        {/* Title Section */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mt: user.role.rolename === 'client' ? 6 : 0, // Add top margin if back button exists
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              display: 'flex',
              borderRadius: '50%',
              alignItems: 'center',
              justifyContent: 'center',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #2c3e50 30%, #3498db 90%)'
                : 'linear-gradient(135deg, #2c3e50 30%, #3498db 90%)',
              boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
            }}
          >
            <KeyIcon sx={{ fontSize: 24, color: 'white' }} />
          </Box>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(45deg, #f3ec78 0%, #af4261 100%)'
                : 'linear-gradient(45deg, #2c3e50 30%, #3498db 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Change Password
          </Typography>
        </Box>

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <RHFTextField
              name="oldPassword"
              type="password"
              label="Old Password"
              InputLabelProps={{
                style: { textTransform: 'uppercase' },
              }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />

            <RHFTextField
              name="newPassword"
              type="password"
              label="New Password"
              InputLabelProps={{
                style: { textTransform: 'uppercase' },
              }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />

            <RHFTextField
              name="confirmNewPassword"
              type="password"
              label="Confirm New Password"
              InputLabelProps={{
                style: { textTransform: 'uppercase' },
              }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />

            <LoadingButton
              type="submit"
              variant="contained"
              loading={isSubmitting}
              sx={{
                py: 1,
                mt: 1,
                fontSize: '0.875rem',
                fontWeight: 600,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #2c3e50 30%, #3498db 90%)'
                  : 'linear-gradient(135deg, #2c3e50 30%, #3498db 90%)',
                '&:hover': {
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #34495e 30%, #2980b9 90%)'
                    : 'linear-gradient(135deg, #34495e 30%, #2980b9 90%)',
                },
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                textTransform: 'uppercase',
                width: '100%',
              }}
            >
              Update Password
            </LoadingButton>
          </Stack>
        </FormProvider>
      </Card>
    </Box>
  );
}
